import Modal from 'components/common/modal';
import {
  EnrichedAccessory,
  StockDot
} from 'components/layout/modals/compatible-combinations-browser-modal';
import ProductPhoto from 'components/products/product-photo';
import { AccessoryProductType } from 'data/types';
import { useEffect, useMemo, useState } from 'react';
import {
  accessorySelectionKey,
  AccessorySelections,
  addSystemsButtonText,
  defaultAccessorySelections
} from 'utils/accessory-selection-defaults';
import { basicProductTitle } from 'utils/basic-product-title';
import {
  AccessoryPick,
  enrichedAccessoryModelText
} from 'utils/ducted-accessory-groups';
import { formatPrice } from 'utils/helpers';
import type { ComboNeedingSelection } from 'utils/quotable-add-pipeline';

type AccessorySelectionModalProps = {
  openState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  // Combinations that need accessory selection, with their resolved accessories
  // grouped by type. Only types with >1 option are included.
  combinationsWithChoices: ComboNeedingSelection[];
  systemCount: number;
  // Called when the user confirms; selections maps combinationId → list of
  // chosen (accessoryId, typeId) pairs, one per multi-option group.
  onConfirm: (selections: Map<number, AccessoryPick[]>) => void;
};

function OptionCard({
  combinationId,
  accessoryTypeCode,
  enriched,
  checked,
  onSelect
}: {
  combinationId: number;
  accessoryTypeCode: string;
  enriched: EnrichedAccessory;
  checked: boolean;
  onSelect: () => void;
}) {
  const { product } = enriched;
  const modelText = enrichedAccessoryModelText(enriched);
  const title = basicProductTitle(product, modelText);
  const priceText =
    product && product.price != null ? formatPrice(product.price) : '—';

  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors ${
        checked
          ? 'border-blue-500 bg-blue-50'
          : 'border-gray-200 bg-white hover:bg-gray-50'
      }`}
    >
      <input
        type='radio'
        className='h-4 w-4 flex-shrink-0 text-blue-600 focus:ring-blue-500'
        name={accessorySelectionKey(combinationId, accessoryTypeCode)}
        checked={checked}
        onChange={onSelect}
        aria-label={modelText}
      />
      {product ? (
        // ProductPhoto's prop is typed against AccessoryProductType | …, but
        // only `.photo` and `.name` are read at runtime — BasicProductData
        // carries both fields, so the structural cast is safe.
        <ProductPhoto
          product={product as unknown as AccessoryProductType}
          size='small'
        />
      ) : (
        <div className='h-8 w-12 flex-shrink-0 rounded-sm bg-gray-200' />
      )}
      <div className='flex min-w-0 flex-1 flex-col'>
        <div className='flex items-center text-sm font-semibold text-gray-900'>
          <span className='line-clamp-2'>{title}</span>
          <StockDot product={product ?? undefined} />
        </div>
        {title !== modelText && (
          <div className='text-xs text-gray-600'>{modelText}</div>
        )}
      </div>
      <div
        className={`flex-shrink-0 text-sm font-semibold ${
          product?.isOnSale ? 'text-red-600' : 'text-gray-900'
        }`}
      >
        {priceText}
      </div>
    </label>
  );
}

export default function AccessorySelectionModal({
  openState,
  combinationsWithChoices,
  systemCount,
  onConfirm
}: AccessorySelectionModalProps) {
  const [open] = openState;
  const initialSelections = useMemo(
    () => defaultAccessorySelections(combinationsWithChoices),
    [combinationsWithChoices]
  );
  const [selections, setSelections] =
    useState<AccessorySelections>(initialSelections);

  useEffect(() => {
    if (open) setSelections(initialSelections);
  }, [open, initialSelections]);

  const totalGroups = useMemo(
    () =>
      combinationsWithChoices.reduce(
        (sum, c) => sum + c.accessoryGroups.length,
        0
      ),
    [combinationsWithChoices]
  );

  const allAnswered = selections.size === totalGroups && totalGroups > 0;

  const handleConfirm = () => {
    if (!allAnswered) return;
    const out = new Map<number, AccessoryPick[]>();
    for (const combo of combinationsWithChoices) {
      const picks: AccessoryPick[] = [];
      for (const group of combo.accessoryGroups) {
        const choice = selections.get(
          accessorySelectionKey(combo.combinationId, group.accessoryTypeCode)
        );
        if (choice) picks.push(choice);
      }
      if (picks.length > 0) out.set(combo.combinationId, picks);
    }
    onConfirm(out);
  };

  return (
    <Modal
      title='Select Required Accessories'
      openState={openState}
      buttonText={addSystemsButtonText(systemCount)}
      onSave={handleConfirm}
      saveDisabled={!allAnswered}
      closeOnSave={false}
      size='xl'
    >
      <div className='flex flex-col gap-4'>
        <p className='text-sm text-gray-600'>
          One or more selected combinations include multiple options for the
          same accessory type. Pick exactly one option per type to continue.
        </p>
        {combinationsWithChoices.map((combo) => (
          <div
            key={combo.combinationId}
            className='rounded-lg border border-gray-200 bg-gray-50 p-3'
          >
            <div className='mb-3 text-sm font-semibold text-gray-700'>
              {combo.systemTypeLabel}
            </div>
            <div className='flex flex-col gap-4'>
              {combo.accessoryGroups.map((group) => {
                const key = accessorySelectionKey(
                  combo.combinationId,
                  group.accessoryTypeCode
                );
                const current = selections.get(key);
                return (
                  <div
                    key={group.accessoryTypeCode}
                    className='flex flex-col gap-2'
                  >
                    <div className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
                      {group.accessoryTypeLabel}
                    </div>
                    <div className='flex flex-col gap-2'>
                      {group.options.map((opt) => {
                        const checked =
                          current?.accessoryId === opt.accessoryId &&
                          current?.typeId === opt.typeId;
                        return (
                          <OptionCard
                            key={`${opt.accessoryId}*${opt.typeId}`}
                            combinationId={combo.combinationId}
                            accessoryTypeCode={group.accessoryTypeCode}
                            enriched={opt.enriched}
                            checked={checked}
                            onSelect={() =>
                              setSelections((prev) => {
                                const next = new Map(prev);
                                next.set(key, {
                                  accessoryId: opt.accessoryId,
                                  typeId: opt.typeId
                                });
                                return next;
                              })
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
